import React from "react";
import type { Checkin } from "~/types";
import { getUrl, type UrlGroup } from "./utils/getUrl";
import { getTitle } from "./utils/getTitle";
import { Content } from "./Content";
import { CardImage } from "./Image";
import { OptionalLink } from "./OptionalLink";
import { Quote } from "./Quote";
import { Text } from "./Text";
import { Title } from "./Title";
import { TopRight } from "./TopRight";

const StyledCard = ({
  children,
  ...props
}: { children: React.ReactNode } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    {...props}
    data-testid="card"
    style={{
      background: "#fff",
      display: "block",
      flex: "1 1 auto",
      margin: "0 auto calc(var(--padding-200) * 2)",
      minWidth: "450px",
      maxWidth: "var(--card-max-width)",
      position: "relative",
    }}
  >
    {children}
  </div>
);

export const Card = ({
  checkin,
  externalVenueLink,
}: {
  checkin: Checkin;
  externalVenueLink?: boolean;
}) => {
  const title = getTitle({ checkin });

  const getUrlGroup = (group: UrlGroup) => {
    return getUrl({ checkin, group, externalVenueLink });
  };

  const renderRegion = () => {
    if (!checkin.area && !checkin.country && !checkin.producer) return null;

    return (
      <Text>
        grown
        {(checkin.area || checkin.country) && <>&nbsp;in&nbsp;</>}
        {checkin.area && (
          <>
            <OptionalLink
              to={getUrlGroup("region")}
              title={`Filter by coffees grown in ${checkin.area}`}
            >
              {checkin.area}
            </OptionalLink>
            {checkin.country && ", "}
          </>
        )}
        {checkin.country && (
          <OptionalLink
            to={getUrlGroup("country")}
            title={`Filter by coffees grown in ${checkin.country}`}
          >
            {checkin.country}
          </OptionalLink>
        )}
        {checkin.producer && (
          <>
            &nbsp;by{" "}
            <OptionalLink
              to={getUrlGroup("producer")}
              title={`Filter by Coffees grown by ${checkin.producer}`}
            >
              {checkin.producer}
            </OptionalLink>
          </>
        )}
      </Text>
    );
  };

  const renderVenue = () => {
    if (!checkin.venue) return null;

    return (
      <Text right>
        at{" "}
        <OptionalLink
          to={getUrlGroup("cafe")}
          target={externalVenueLink ? "_blank" : undefined}
        >
          {checkin.venue.name}
        </OptionalLink>
        <br />
        in&nbsp;
        {checkin.venue.location?.city && (
          <>
            <OptionalLink
              to={getUrlGroup("location/city")}
              title={`Checkins in ${checkin.venue.location.city}`}
            >
              {checkin.venue.location.city}
            </OptionalLink>
            {(checkin.venue.location?.state ||
              checkin.venue.location?.country) &&
              ", "}
          </>
        )}
        {checkin.venue.location?.state && (
          <>
            <OptionalLink
              to={getUrlGroup("location/state")}
              title={`Checkins in ${checkin.venue.location.state}`}
            >
              {checkin.venue.location.state}
            </OptionalLink>
            {checkin.venue.location?.country && ", "}
          </>
        )}
        {checkin.venue.location?.country && (
          <OptionalLink
            to={getUrlGroup("location/country")}
            title={`Checkins in ${checkin.venue.location.country}`}
          >
            {checkin.venue.location.country}
          </OptionalLink>
        )}
      </Text>
    );
  };

  const renderDetails = () => {
    if (
      !checkin.brewingMethod &&
      !checkin.processingMethod &&
      !checkin.varietal
    )
      return null;

    const details = [
      checkin.brewingMethod && (
        <OptionalLink
          to={getUrlGroup("brewing-method")}
          title={`Filter by brewing method: ${checkin.brewingMethod}`}
        >
          {checkin.brewingMethod}
        </OptionalLink>
      ),

      checkin.processingMethod && (
        <OptionalLink
          to={getUrlGroup("processing-method")}
          title={`Filter by processing method: ${checkin.processingMethod}`}
        >
          {checkin.processingMethod}
        </OptionalLink>
      ),

      checkin.varietal && (
        <OptionalLink
          to={getUrlGroup("varietal")}
          title={`Filter by varietal: ${checkin.varietal}`}
        >
          {checkin.varietal}
        </OptionalLink>
      ),
    ].filter((rendered) => rendered);

    return (
      <Text>
        {details.map((rendered, index) => (
          <React.Fragment key={`details-${index}`}>
            {rendered}

            {index + 1 < details.length && <> • </>}
          </React.Fragment>
        ))}
      </Text>
    );
  };

  return (
    <StyledCard key={checkin.id} data-id={`card-${checkin.id}`}>
      <TopRight createdAt={checkin.createdAt} like={!!checkin.like} />

      <Content>
        <Title>
          <OptionalLink to={getUrlGroup("checkin")}>{title}</OptionalLink>
        </Title>

        {renderDetails()}
        {renderRegion()}

        {checkin.roaster && (
          <Text>
            roasted by{" "}
            <OptionalLink
              to={getUrlGroup("roaster")}
              title={`Filter by the roaster ${checkin.roaster}`}
            >
              {checkin.roaster}
            </OptionalLink>
          </Text>
        )}

        <Quote>{checkin.shout}</Quote>

        {renderVenue()}
      </Content>

      <CardImage photo={checkin.photos?.items?.[0]} title={title} />
    </StyledCard>
  );
};
